import React from 'react';
import {
  Box,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
} from '@mui/material';

/**
 * Reusable Pagination Controls Component
 *
 * @param {number} count - Total number of items
 * @param {number} page - Current page number (1-indexed)
 * @param {number} pageSize - Items per page
 * @param {number} totalPages - Total number of pages
 * @param {function} onPageChange - Callback when page changes: (event, newPage) => void
 * @param {function} onPageSizeChange - Callback when page size changes: (event) => void
 * @param {array} pageSizeOptions - Available page sizes (default: [10, 25, 50, 100])
 */
function PaginationControls({
  count = 0,
  page = 1,
  pageSize = 25,
  totalPages = 1,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
}) {
  // Calculate the range of items currently displayed
  const startItem = totalPages > 0 ? (page - 1) * pageSize + 1 : 0;
  const endItem = Math.min(page * pageSize, count);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mt: 3,
        flexWrap: 'wrap',
        gap: 2,
      }}
    >
      {/* Results info */}
      <Typography variant="body2" color="textSecondary">
        {count > 0
          ? `Showing ${startItem}-${endItem} of ${count} items`
          : 'No items to display'
        }
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Page size selector */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="page-size-label">Per page</InputLabel>
          <Select
            labelId="page-size-label"
            id="page-size-select"
            value={pageSize}
            label="Per page"
            onChange={onPageSizeChange}
          >
            {pageSizeOptions.map((size) => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Pagination buttons */}
        {totalPages > 1 && (
          <Pagination
            count={totalPages}
            page={page}
            onChange={onPageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        )}
      </Box>
    </Box>
  );
}

export default PaginationControls;
